import React from 'react';
import {
  Box, Container, Grid, Typography, Divider, Paper,
  Avatar, Button, Chip,
} from '@mui/material';
import {
  CalendarMonth, Person, ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { stats } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

// ─── Sub-category cards (matching the screenshot layout) ──────────────────────

interface InfoCard {
  icon: string;
  titleEn: string;
  titleFr: string;
  bgColor: string;
}

const infoCards: InfoCard[] = [
  { icon: '🏥', titleEn: 'Our Mission & Vision', titleFr: 'Mission & Vision', bgColor: '#0B4F8A' },
  { icon: '🏆', titleEn: 'Our Values & Ethics', titleFr: 'Valeurs & Éthique', bgColor: '#1565C0' },
  { icon: '📅', titleEn: 'Hospital History', titleFr: 'Histoire de l\'Hôpital', bgColor: '#0277BD' },
  { icon: '🤝', titleEn: 'Community Commitment', titleFr: 'Engagement Communautaire', bgColor: '#01579B' },
];

const SubCard: React.FC<{ card: InfoCard; lang: string; onClick: () => void }> = ({ card, lang, onClick }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
    <Box
      sx={{
        height: 160,
        background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.5rem',
      }}
    >
      {card.icon}
    </Box>
    <Box sx={{ p: 2 }}>
      <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
          {lang === 'en' ? card.titleEn : card.titleFr}
        </Typography>
      </Box>
      <Typography
        onClick={onClick}
        sx={{ color: 'primary.main', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}
      >
        {lang === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowForward sx={{ fontSize: 14 }} />
      </Typography>
    </Box>
  </Box>
);

// ─── Leadership sidebar ───────────────────────────────────────────────────────

const LeaderSidebar: React.FC<{ lang: string; onAppt: () => void }> = ({ lang, onAppt }) => (
  <Paper elevation={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0, overflow: 'hidden', position: { md: 'sticky' }, top: { md: 90 } }}>
    <Box sx={{ height: 220, background: 'linear-gradient(145deg, #0B4F8A22 0%, #0B4F8A44 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Avatar sx={{ width: 130, height: 130, bgcolor: '#0B4F8A', fontSize: '2.5rem', fontWeight: 800, border: '4px solid white', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
        EN
      </Avatar>
    </Box>
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600} mb={0.25}>
        {lang === 'en' ? 'Medical Director' : 'Directeur Médical'}
      </Typography>
      <Typography variant="h6" fontWeight={800} color="text.primary" mb={0.5} lineHeight={1.3}>
        Dr. Emmanuel Nkouam
      </Typography>
      <Divider sx={{ my: 2 }} />
      {[
        lang === 'en' ? 'General Surgery' : 'Chirurgie Générale',
        lang === 'en' ? 'Medical Leadership' : 'Direction Médicale',
        lang === 'en' ? 'Surgical Oncology' : 'Oncologie Chirurgicale',
        lang === 'en' ? 'Hospital Administration' : 'Administration Hospitalière',
      ].map((s, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
          <Person sx={{ fontSize: 15, color: 'primary.main', mt: 0.3, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{s}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <Button variant="contained" size="small" fullWidth startIcon={<CalendarMonth />} onClick={onAppt}
        sx={{ textTransform: 'none', borderRadius: 0, fontWeight: 700 }}>
        {lang === 'en' ? 'Book Appointment' : 'Prendre Rendez-vous'}
      </Button>
    </Box>
  </Paper>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const AboutPage: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const lang = language;

  const timeline = [
    { year: '1978', en: 'Hospital founded by the Cameroonian Government to serve the population of the West Region', fr: 'L\'hôpital est fondé par le Gouvernement camerounais pour servir la population de la Région de l\'Ouest' },
    { year: '1990', en: 'Expansion of the maternity and pediatric wards with 80 new beds and a neonatal care unit', fr: 'Extension de la maternité et du service pédiatrique avec 80 nouveaux lits et une unité de soins néonatals' },
    { year: '2005', en: 'Introduction of modern digital radiology, automated laboratory analyzers, and ICU establishment', fr: 'Introduction de la radiologie numérique moderne, d\'automates de laboratoire et création de l\'USI' },
    { year: '2015', en: 'ICU and Emergency Department fully upgraded with cardiac monitoring, defibrillators, and trauma bay', fr: 'Mise à niveau complète de l\'USI et des Urgences avec monitorage cardiaque, défibrillateurs et salle de trauma' },
    { year: '2020', en: 'COVID-19 designated treatment center, telemedicine platform launched for district hospital consultations', fr: 'Centre de traitement désigné COVID-19, lancement de la télémédecine pour les consultations des hôpitaux de district' },
    { year: '2024', en: 'New maternity wing inaugurated: 40 additional beds, modern delivery suite with CTG monitoring, and neonatal care unit', fr: 'Inauguration du nouveau pavillon maternité : 40 lits supplémentaires, salle d\'accouchement avec monitorage CTG et unité néonatale' },
  ];

  const values = [
    { icon: '❤️', en: 'Patient-Centered Care', fr: 'Soins Centrés sur le Patient' },
    { icon: '🏆', en: 'Clinical Excellence', fr: 'Excellence Clinique' },
    { icon: '🤝', en: 'Integrity & Ethics', fr: 'Intégrité & Éthique' },
    { icon: '🌍', en: 'Community Service', fr: 'Service Communautaire' },
    { icon: '💡', en: 'Continuous Innovation', fr: 'Innovation Continue' },
    { icon: '👥', en: 'Teamwork & Respect', fr: 'Travail d\'Équipe & Respect' },
  ];

  const community = [
    { icon: '🦟', en: 'Malaria Prevention Campaigns', fr: 'Campagnes de Prévention du Paludisme' },
    { icon: '🤱', en: 'Maternal Health Programs', fr: 'Programmes Santé Maternelle' },
    { icon: '💉', en: 'National Vaccination Drives', fr: 'Campagnes Nationales de Vaccination' },
    { icon: '🩸', en: 'Blood Donation Campaigns', fr: 'Campagnes de Don de Sang' },
    { icon: '🎓', en: 'Medical Staff Training & CME', fr: 'Formation Médicale Continue' },
    { icon: '🏘️', en: 'Rural Health Outreach', fr: 'Soins de Santé Ruraux' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Hero ── */}
      <PageHero
        title={lang === 'en' ? 'About Our Hospital' : 'À Propos de Notre Hôpital'}
        subtitle={lang === 'en'
          ? 'Serving the people of the West Region with clinical excellence since 1978'
          : 'Au service de la population de la Région de l\'Ouest avec excellence clinique depuis 1978'}
        breadcrumbs={[
          { label: lang === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: lang === 'en' ? 'About Us' : 'À Propos' },
        ]}
        accentColor="#0B4F8A"
      />

      {/* ── Sub-category cards ── */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {infoCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <SubCard card={card} lang={lang} onClick={() => {}} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Main content + Sidebar ── */}
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>

          {/* Left column */}
          <Grid item xs={12} md={8}>

            {/* Mission section */}
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {lang === 'en' ? 'MISSION & VISION OF HÔPITAL RÉGIONAL DE BAFOUSSAM' : 'MISSION & VISION DE L\'HÔPITAL RÉGIONAL DE BAFOUSSAM'}
            </Typography>

            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={1.5}>
              {lang === 'en'
                ? 'Hôpital Régional de Bafoussam is a public tertiary-level healthcare institution operating under the authority of the Cameroonian Ministry of Public Health. Founded in 1978, our hospital serves as the principal referral center for the West Region of Cameroon, providing expert medical care to a catchment population of over 2 million people across the region\'s 20 health districts.'
                : 'L\'Hôpital Régional de Bafoussam est un établissement de santé public de niveau tertiaire placé sous la tutelle du Ministère de la Santé Publique du Cameroun. Fondé en 1978, notre hôpital est le principal centre de référence de la Région de l\'Ouest du Cameroun, dispensant des soins médicaux experts à une population de plus de 2 millions de personnes dans les 20 districts sanitaires de la région.'}
            </Typography>

            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>
              {lang === 'en'
                ? 'Our mission is to provide equitable, compassionate, and evidence-based medical care to every patient — regardless of social, economic, or geographical background — while training the next generation of healthcare professionals and contributing to the advancement of medical practice in Cameroon.'
                : 'Notre mission est de prodiguer des soins médicaux équitables, bienveillants et fondés sur des preuves à chaque patient — indépendamment de son contexte social, économique ou géographique — tout en formant la prochaine génération de professionnels de santé et en contribuant à l\'avancement de la pratique médicale au Cameroun.'}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* History section */}
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 3, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {lang === 'en' ? 'INSTITUTIONAL HISTORY & MILESTONES' : 'HISTOIRE INSTITUTIONNELLE & JALONS'}
            </Typography>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {stats.map((stat, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Box sx={{ p: 2, border: '1.5px solid', borderColor: 'primary.main', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">{stat.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                      {lang === 'en' ? stat.labelEn : stat.labelFr}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Timeline */}
            {timeline.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
                <Box sx={{ flexShrink: 0, textAlign: 'center', minWidth: 56 }}>
                  <Chip label={item.year} sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700, fontSize: '0.75rem' }} />
                  {i < timeline.length - 1 && (
                    <Box sx={{ width: 2, height: 28, bgcolor: 'divider', mx: 'auto', mt: 0.5 }} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" pt={0.75} lineHeight={1.8}>
                  {lang === 'en' ? item.en : item.fr}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 3 }} />

            {/* Values */}
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 3, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {lang === 'en' ? 'OUR CORE VALUES' : 'NOS VALEURS FONDAMENTALES'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {values.map((val, i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center', height: '100%' }}>
                    <Typography fontSize="2rem" mb={1}>{val.icon}</Typography>
                    <Typography variant="body2" fontWeight={700} lineHeight={1.4}>
                      {lang === 'en' ? val.en : val.fr}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Community */}
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {lang === 'en' ? 'COMMUNITY HEALTH COMMITMENT' : 'ENGAGEMENT EN SANTÉ COMMUNAUTAIRE'}
            </Typography>

            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>
              {lang === 'en'
                ? 'Beyond our hospital walls, Hôpital Régional de Bafoussam is deeply committed to the health of the communities we serve. Through structured outreach programs, partnerships with the Ministry of Public Health, and collaboration with international NGOs, we extend preventive and curative care to underserved populations across the West Region.'
                : 'Au-delà de nos murs, l\'Hôpital Régional de Bafoussam est profondément engagé envers la santé des communautés que nous servons. À travers des programmes de sensibilisation structurés, des partenariats avec le Ministère de la Santé Publique et la collaboration avec des ONG internationales, nous étendons les soins préventifs et curatifs aux populations mal desservies de la Région de l\'Ouest.'}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {community.map((item, i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center', height: '100%' }}>
                    <Typography fontSize="1.75rem" mb={0.75}>{item.icon}</Typography>
                    <Typography variant="caption" fontWeight={600} lineHeight={1.5} display="block">
                      {lang === 'en' ? item.en : item.fr}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* CTA */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" startIcon={<CalendarMonth />}
                onClick={() => navigate('/appointment')}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 0 }}>
                {lang === 'en' ? 'Book an Appointment' : 'Prendre un Rendez-vous'}
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate('/contact')}
                sx={{ textTransform: 'none', borderRadius: 0 }}>
                {lang === 'en' ? 'Contact Us' : 'Nous Contacter'}
              </Button>
            </Box>
          </Grid>

          {/* Right — Leadership sidebar */}
          <Grid item xs={12} md={4}>
            <LeaderSidebar lang={lang} onAppt={() => navigate('/appointment')} />

            {/* Accreditation box */}
            <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={2} color="primary.main">
                {lang === 'en' ? 'Accreditation & Quality' : 'Accréditation & Qualité'}
              </Typography>
              {[
                lang === 'en' ? 'Ministry of Public Health — Certified Regional Hospital' : 'Ministère de la Santé Publique — Hôpital Régional Certifié',
                lang === 'en' ? 'WHO Essential Medicines List compliance' : 'Conformité à la liste des médicaments essentiels OMS',
                lang === 'en' ? 'National EPI (Expanded Programme on Immunization)' : 'Programme National PEV de Vaccination',
                lang === 'en' ? 'Accredited Teaching Hospital for FMSB' : 'Hôpital d\'Enseignement Accrédité FMSB',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Typography sx={{ color: 'success.main', fontSize: '1rem', lineHeight: 1.4, flexShrink: 0 }}>✓</Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default AboutPage;
